import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';

import { useDevices } from 'features/device/devices/hooks/use-devices';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemEventStatus, SystemEventType } from 'features/systems/domain/models/system';
import { FinishScanSystemEventResponse } from 'features/systems/IS32/domain/models/finish-scan-system-event-response';
import { ReadIS32ConfigurationResponse } from 'features/systems/IS32/domain/models/read-IS32-configuration-unit-response';
import { useLazyGetIS32Configuration, useScanConfiguration } from 'features/systems/IS32/domain/queries/IS32-queries';

export interface IS32Hook {
  startScanIsLoading: boolean;

  inProgressPopupIsOpen: boolean;
  succeededPopupIsOpen: boolean;
  failedPopupIsOpen: boolean;

  lastScanSucceededResult: FinishScanSystemEventResponse | undefined;

  closePopup: () => void;

  startScan: (systemId: string) => void;

  readIS32ConfigurationIsLoading: boolean;
  readIS32ConfigurationData: ReadIS32ConfigurationResponse | undefined;
  refetch: () => void;

  selectedDeviceId: string | undefined;
  openDeviceDetailsByExternalId: (partialExternalId: string) => void;
  openDeviceDetailsByDeviceId: (id: string) => void;
}

const useIS32 = (): IS32Hook => {
  const { t } = useTranslation('IS32');
  const { showConfirmationPopUp } = use(ConfirmationPopupProviderContext);

  const {
    systemsDetailsHook: { selectedSystem, viewingMode, refreshSystem },
  } = useSystemsContextProvider();
  const { openDeviceDetailsPopup } = useDevices();

  const { mutate: triggerScanConfiguration, isPending: scanIsLoading, isSuccess: scanIsSuccess } = useScanConfiguration();

  const {
    refetch: readIS32Configuration,
    isLoading: readIS32ConfigurationIsLoading,
    data: readIS32ConfigurationData,
  } = useLazyGetIS32Configuration(selectedSystem?.id ?? '');

  const [previousLastSystemEventStatus, setPreviousLastSystemEventStatus] = useState<SystemEventStatus>();

  const [inProgressPopupIsOpen, setInProgressPopupIsOpen] = useState<boolean>(false);
  const [succeededPopupIsOpen, setSucceededPopupIsOpen] = useState<boolean>(false);
  const [failedPopupIsOpen, setFailedPopupIsOpen] = useState<boolean>(false);
  const [lastScanSucceededResult, setLastScanSucceededResult] = useState<FinishScanSystemEventResponse>();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();

  useEffect(() => {
    if (selectedSystem?.id) {
      readIS32Configuration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  useEffect(() => {
    if (!scanIsLoading && scanIsSuccess) {
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanIsLoading, scanIsSuccess]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (selectedSystem?.lastSystemEvent?.systemEventStatus === SystemEventStatus.InProgress) {
      intervalId = setInterval(() => {
        refreshSystem();
      }, 10000);
    } else {
      clearInterval(intervalId);
    }

    return (): void => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem?.lastSystemEvent?.systemEventStatus]);

  useEffect(() => {
    const currentEventType = selectedSystem?.lastSystemEvent?.systemEventType;
    const currentEventStatus = selectedSystem?.lastSystemEvent?.systemEventStatus;

    if (currentEventType === SystemEventType.ScanIS32Configuration && previousLastSystemEventStatus !== currentEventStatus) {
      if (currentEventStatus === SystemEventStatus.InProgress) {
        setInProgressPopupIsOpen(true);
        setSucceededPopupIsOpen(false);
        setFailedPopupIsOpen(false);
      } else if (previousLastSystemEventStatus === SystemEventStatus.InProgress && currentEventStatus === SystemEventStatus.Succeeded) {
        const numberOfNewDevices = JSON.parse(selectedSystem?.lastSystemEvent?.response ?? '').NumberOfNewDevices;

        setLastScanSucceededResult({ numberOfNewDevices });

        setInProgressPopupIsOpen(false);
        setSucceededPopupIsOpen(true);
        setFailedPopupIsOpen(false);
      } else if (previousLastSystemEventStatus === SystemEventStatus.InProgress && currentEventStatus === SystemEventStatus.Failed) {
        setInProgressPopupIsOpen(false);
        setSucceededPopupIsOpen(false);
        setFailedPopupIsOpen(true);
      }

      setPreviousLastSystemEventStatus(currentEventStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem?.lastSystemEvent?.systemEventStatus, previousLastSystemEventStatus]);

  const startScan = (systemId: string): void => {
    showConfirmationPopUp(
      () => {
        triggerScanConfiguration(systemId);
      },
      t('startScanConfirmationPopup.title'),
      t('startScanConfirmationPopup.body'),
      t('startScanConfirmationPopup.primaryActionButtonText'),
      t('startScanConfirmationPopup.secondaryActionButtonText'),
    );
  };

  const closePopup = (): void => {
    setInProgressPopupIsOpen(false);
    setSucceededPopupIsOpen(false);
    setFailedPopupIsOpen(false);
  };

  const openDeviceDetailsByExternalId = (partialExternalId: string): void => {
    if (viewingMode === 'viewing') {
      const externalId = `${selectedSystem?.macAddress}_${partialExternalId}`;

      setSelectedDeviceId(selectedSystem?.devices?.find((x) => x.externalId === externalId)?.id);
      openDeviceDetailsPopup(false);
    }
  };

  const openDeviceDetailsByDeviceId = (id: string): void => {
    if (viewingMode === 'viewing') {
      setSelectedDeviceId(id);
      openDeviceDetailsPopup(false);
    }
  };

  const refetch = (): void => {
    readIS32Configuration();
  };

  return {
    startScan,

    closePopup,

    startScanIsLoading: scanIsLoading,
    inProgressPopupIsOpen,
    failedPopupIsOpen,
    succeededPopupIsOpen,
    lastScanSucceededResult,

    readIS32ConfigurationIsLoading,
    readIS32ConfigurationData,
    refetch,

    selectedDeviceId,
    openDeviceDetailsByExternalId,
    openDeviceDetailsByDeviceId,
  };
};

export default useIS32;
